import React, {useState, useRef, useEffect} from 'react';
import {createPortal} from 'react-dom';
import styled from 'styled-components';
import useLockHTMLScroll from '@stencil/lock-body-scroll-hook';
import CloseCircleFillIcon from '../../../verticals/hotels-dweb/src/stories/Icons/CloseCircleFillIcon';

import {defaultTheme} from '@stencil/build-theme';
import ClientOnly from '@stencil/generic-client-only-component';

/**
 * 📝: this can be used as 'usePortal' too
 *  named 'useModalPortal' because there is
 *  no other usecase for portals than modals
 *  if you need any different kind of portal
 *  rename this as 'usePortal' and use it
 */

// function useModalPortal(id: string = 'modal-root') {
//   if (typeof document === 'undefined') return;

//   const rootElemRef = useRef<unknown>();

//   function getRootElem() {
//     if (!rootElemRef.current) {
//       rootElemRef.current = document.createElement('div');
//     }
//     return rootElemRef.current as HTMLElement;
//   }

//   useEffect(function setUpModalPortalContainer() {
//     const parentElem = document.getElementById(id) as HTMLElement; // we know 'modal-root' is always going to be there

//     parentElem.appendChild(getRootElem());
//     // This function is run on unmount
//     return function removeElement() {
//       getRootElem().remove();
//     };
//   }, []);

//   return getRootElem();
// }

function useGetModalRoot(id = 'modal-root') {
  if (typeof document === 'undefined') return null;
  const rootElemRef = useRef<HTMLElement | null>(null);

  function getModalRoot() {
    if (!rootElemRef.current) {
      rootElemRef.current = document.createElement('div');
      rootElemRef.current.setAttribute('id', id);
    }

    return rootElemRef.current as HTMLElement;
  }

  let modalRoot = document && document.getElementById(id);

  useEffect(() => {
    const bodyEl = document.body;
    modalRoot = document.getElementById(id);

    if (!modalRoot) {
      bodyEl.appendChild(getModalRoot());
    }
  }, []);

  return modalRoot;
}

type ModalStyleProps = {
  height?: string;
  width?: string;
  position?: string;
  top?: string | number;
  bottom?: string | number;
  left?: string | number;
  transform?: string;
  borderRadius?: string;
};

const S = {
  ModalOverlay: styled.div`
    background: rgba(0, 0, 0, 0.4);
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 1000;
  `,
  Modal: styled.div(props => {
    const conditionalProps: {
      position?: string;
      bottom?: string | number;
    } = {};

    if (props.position) {
      conditionalProps.position = props.position;
    }

    if (props.hasOwnProperty('bottom')) {
      conditionalProps.bottom = props.bottom;
    }

    return {
      top: props.hasOwnProperty('top') || '50%',
      left: props.hasOwnProperty('left') || '50%',
      position: 'fixed',
      borderRadius: props.borderRadius || '0.8rem',
      width: props.width || '60vw',
      height: props.height || 'auto',
      transform: props.transform || 'translate(-50%, -50%)',
      background: 'white',
      maxWidth: '100%',
      maxHeight: '100%',
      zIndex: 1010,
      ...conditionalProps,
    };
  }),
  ModalGuts: styled.div(
    props => `
    // position: absolute;
    // top: 0;
    // left: 0;
    // width: 100%;
    // background: ${props.theme.colors.goWhite.base};
    /* height: 100%; */
    overflow: auto;
    height: 100%;
  `,
  ),
  CloseButtonWrapper: styled.div`
    position: absolute;
    z-index: 1;
    top: -20px;
    right: -25px;
    border: 0;

    svg {
      transform: scale(2);
    }

    svg g path:nth-child(2) {
      fill: ${defaultTheme.colors.goWhite.base};
    }
  `,
};

/**
 * an ease hook for straight forward
 * managing of modal close and open state.
 * if you want custom handling of opening or closing
 * of your <Modal> component, you should not use this
 * hook, and handle Modal state on your own, by passing
 * your `isOpen` state to <Modal> component
 */

export const useModal = (initState?: boolean) => {
  const [isModalOpen, setModalStateTo] = useState(initState || false);

  const closeModal = () => setModalStateTo(false);
  const openModal = () => setModalStateTo(true);

  return {
    isModalOpen,
    closeModal,
    openModal,
  };
};

/**
 * ModalOverlay is not passed any `overlayProps` prop
 * from <Modal> component right now. If there is need for that
 * you need to take an extra prop for <Modal> component
 * and pass those Modal Overlay styles in 'overlayProps' prop here
 */
export const ModalOverlay = ({
  children,
  overlayProps = {},
}: {
  children: React.ReactNode;
  overlayProps?: object;
}) => <S.ModalOverlay {...overlayProps}>{children}</S.ModalOverlay>;

const noop = () => {};

const Modal: React.FC<{
  children: React.ReactNode;
  isOpen?: boolean;
  closeModal?: (event?: React.MouseEvent<HTMLElement>) => void;
  onCloseModal?: () => void;
  onModalOpen?: () => void;
  modalStyles?: ModalStyleProps;
  CloseButton?: React.FC<any>;
  hideCloseButton?: boolean;
}> = ({
  children,
  isOpen = false,
  closeModal,
  onCloseModal = noop,
  onModalOpen = noop,
  modalStyles = {},
  CloseButton,
  hideCloseButton = false,
}) => {
  /******************************************************************************************** */
  /**
   * Right now we are having separate 'containers' for every
   * modal on the page. If there are 6 EMI modals and 6 Cancellation Policy
   * Modals, we make 12 containers for them, and open and close Modals into them
   * This should not be a performance problem, because
   *
   * 1. They are empty divs
   * 2. They are generated on runtime and appended to 'modal-root' in useEffect
   * 3. They are limited
   *
   * Their benefit is that they keep all modals containerized.
   *
   * However one could, if one wants ( if the number of modals on the page increases more than 25 say)
   * directly use 'modal-root' to render all the modals in, making sure that while closing one modal
   * all the children get removed from 'modal-root'
   */
  // const target = useModalPortal();
  /********************************************************************************************** */

  // this will be available because it comes from the template
  const target = useGetModalRoot();

  // close modal on escape keydown
  useEffect(() => {
    function closeModalOnEscape(e: KeyboardEvent) {
      const {key, keyCode} = e;

      if (key === 'Escape' || keyCode === 27) {
        closeModal();
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', closeModalOnEscape);
    }

    return () => {
      if (!isOpen) {
        document.removeEventListener('keydown', closeModalOnEscape);
      }
    };
  }, [isOpen]);

  useLockHTMLScroll({
    stateOnWhichLockToggles: isOpen,
  });

  if (!isOpen) {
    return null;
  }

  function _onClose() {
    if (onCloseModal) {
      onCloseModal();
    }
    closeModal();
  }

  if (!target) {
    return null;
  }

  return (
    <ClientOnly
      onClick={e => {
        e.stopPropagation();
      }}
    >
      {createPortal(
        <>
          <S.ModalOverlay id="modal-overlay" onClick={_onClose} />
          <S.Modal {...modalStyles}>
            {/* things that don't scroll go here */}
            {/* take from prop, have a default */}
            {!hideCloseButton && (
              <S.CloseButtonWrapper onClick={_onClose}>
                {CloseButton ? (
                  <CloseButton />
                ) : (
                  <CloseCircleFillIcon fill={defaultTheme.colors.goWhite.base} />
                ) // default close icon
                }
              </S.CloseButtonWrapper>
            )}
            {/* things that could scroll depending on the content go inside S.ModalGuts */}
            <S.ModalGuts>{children}</S.ModalGuts>
            {/* <span>close</span> */}
          </S.Modal>
        </>,
        target,
      )}
    </ClientOnly>
  );
};

export default Modal;
